import Claims, { ClaimStatus } from '@/services/Claims';

export type Brand = 'insurecareteams' | 'mmic' | 'umia' | 'amic';

type Guid = string;

/**
 * `ClaimBase` contains the fields that are found in all versions of a claim.
 */
interface ClaimBase {
	additionalFields?: {
		claimConsultantEmail?: string;
		claimConsultantName?: string;
		claimConsultantPhone?: string;
		lossType?: string;
	};

	/**
	 * `assignee` is a deprecated field. Prefer `additionalFields.claimConsultant*` instead.
	 *
	 * @deprecated
	 */
	assignee?: string;

	/**
	 * Yes, this data may exist both here and at `claim_data.brand`. Give this one precedence
	 * because it participates in a DynamoDB GSI and therefore is more important. Hopefully they
	 * never differ when both are set, but it has been observed that one can exist without the
	 * other also existing.
	 */
	brand?: Brand;

	/**
	 * All claim records have a `claim_data` sub-object. Its contents will differ based on the
	 * version, but it will have the following fields in common across all versions.
	 */
	claim_data: {
		/**
		 * Yes, this data may exist both at the root and here. Give the root `brand` property
		 * precedence over this one because the former participates in a DynamoDB GSI and
		 * therefore is more important. Hopefully they never differ when both are set, but it has
		 * been observed that one can exist without the other also existing.
		 */
		brand?: Brand;
		nameOfInsured?: string;
		usState?: string;
	};

	/** The primary partition key. */
	claim_id: Guid;

	comments?: string;

	real_claim_id?: string;
	/**
	 * All claim records have a `date_submitted` property formatted like
	 * 'Sun Dec 22 2019 18:07:14 GMT+0000 (UTC)'.
	 */
	date_submitted: string;

	/** A valid email address or the string `'public'`. */
	email: string | 'public';

	/**
	 * Another GSI partition key. There are some instances containing the string `'null'`.
	 */
	org_id?: Guid | 'null';

	setup?: boolean;

	/** GSI partition key. */
	sub: Guid;
}

/**
 * `ClaimViewModel` provides an adapter API for a single underlying claim, abstracting which
 * version that claim may be. This saves UI code from the burdens of correctly handling claim
 * variations. Currently this class is fleshed out just enough for Claims.vue, but in the future it
 * could be enhanced so that OldClaimsFormSingle.vue and NewClaimsFormSingle.vue could use it, and
 * maybe at that point those two would be so similar that they could be consolidated into a single
 * component.
 */
export class ClaimViewModel {
	protected constructor(protected readonly claim: ClaimBase) {}

	public static create(claim: ClaimBase): ClaimViewModel {
		return new ClaimViewModel(claim as ClaimBase);
	}

	public get assignedClaimConsultant(): string | undefined {
		return (
			this.claim.additionalFields?.claimConsultantName ||
			this.claim.additionalFields?.claimConsultantEmail ||
			this.claim.assignee
		);
	}

	public get brand(): Brand | undefined {
		return this.claim.brand || this.claim.claim_data.brand;
	}

	public get comments(): string | undefined {
		return this.claim.comments;
	}

	public get claimId(): Guid {
		return this.claim.claim_id;
	}

	public get dateSubmitted(): string {
		return this.claim.date_submitted;
	}

	public get lossType(): string | undefined {
		return this.claim.additionalFields?.lossType;
	}

	public get setup(): boolean | undefined {
		return this.claim.setup;
	}

	public get status(): ClaimStatus {
		return Claims.getClaimStatus(this.claim);
	}

	public get nameOfInsured(): string {
		return this.claim.claim_data.nameOfInsured || this.claim.email;
	}

	public get usState(): string | undefined {
		return this.claim.claim_data.usState;
	}
	public get claimNumber(): string | undefined {
		return this.claim.real_claim_id;
	}
}
